import { useEffect, useState } from "react";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import CustomLoader from "@axvdex/components/common/CustomLoader";
import useLanguage from "@axvdex/hooks/useLanguage";
import { useAppDispatch, useAppSelector } from "@axvdex/state";
import SalesSelectGrid from "@axvdex/components/outbid/SalesSelectGrid";
import SolanaSalesSelectGrid, { FilterOption } from "@axvdex/components/outbid/solana/SolanaSalesSelectGrid";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";
import SaleMain from "@axvdex/components/outbid/SaleMain";
import { updateUserSaleBids } from "@axvdex/state/outbid/outbidThunks";
import { useOutbidLocalStorageState } from "@axvdex/state/outbid/outbidPersist";
import { useGetOutbidSales } from "@axvdex/hooks/solana/useOutbid";
import { OutbidSale } from "@axvdex/types/solana/outbid";
import SolanaSaleMain from "@axvdex/components/outbid/solana/SolanaSaleMain";
import styles from "../styles/Outbid.module.scss";
import MainLayout from "../layouts/MainLayout";

const Outbid = () => {
  if (process.env.REACT_APP_SOLANA_ENABLED === "1") {
    return <OutbidSolana />;
  }

  const outbidState = useAppSelector(state => state.outbid);
  if (outbidState.initLoading) {
    return <CustomLoader isFixed={true} showGRVT8 />;
  }
  return <OutbidSalesComponent />;
};

function OutbidSalesComponent() {
  const { i18 } = useLanguage();
  const dispatch = useAppDispatch();
  const routeParams = useParams();
  const walletInfo = useAppSelector(selectWalletInfo);
  const outbidState = useAppSelector(state => state.outbid);
  const [outbidLocalStorageState, setOutbidLocalStorageState] = useOutbidLocalStorageState();

  const topFilterOptions = [
    {
      label: i18("All", "pools.topFilter.all"),
      id: "all",
    },
    {
      label: i18("Upcoming", "pools.topFilter.all"),
      id: "upcoming",
    },
    {
      label: i18("Live", "pools.topFilter.all"),
      id: "live",
    },
    {
      label: i18("Vesting", "pools.topFilter.all"),
      id: "vesting",
    },
    {
      label: i18("Completed", "pools.topFilter.all"),
      id: "completed",
    },
  ];

  const [topFilter, setTopFilter] = useState(topFilterOptions[0]);
  const [outbidSaleSelected, setOutbidSaleSelected] = useState(null);

  useEffect(() => {
    if (routeParams && routeParams.label) setOutbidSaleSelected(routeParams.label);
    // else if (!outbidSaleSelected && 0 < Object.keys(outbidSales).length)
    //   setOutbidSaleSelected(Object.keys(outbidSales)[0]);
  }, [outbidState.outbids]);

  const getGridUserBids = async () => {
    const promises = [];
    for (const sale of Object.values(outbidState.outbids)) {
      if (Object.keys(sale.salesState).length > 0) {
        for (const saleAddr of Object.keys(sale.salesState)) {
          promises.push(
            dispatch(
              updateUserSaleBids({
                walletAddress: walletInfo.walletAddress,
                saleAddress: saleAddr,
                withDetails: true,
              })
            )
          );
        }
      }
    }

    await Promise.all(promises);
  };

  useEffect(() => {
    if (walletInfo.isConnected && outbidState.outbids && !outbidState.outbidUser.salesBids) getGridUserBids();
  }, [outbidState.outbids, outbidState.outbidUser.salesBids, walletInfo]);

  return (
    <MainLayout
      pageClass={clsx(styles.sales, "sales")}
      headTitle={i18("Outbid", "outbid.headTitle")}
      headDescription={i18("Outbid", "outbid.headDescription")}
    >
      <div style={{ maxWidth: "64em", width: "100%", alignSelf: "center" }}>
        {!outbidSaleSelected && (
          <SalesSelectGrid
            topFilterOptions={topFilterOptions}
            topFilter={topFilter}
            setTopFilter={setTopFilter}
            setOutbidSaleSelected={setOutbidSaleSelected}
          />
        )}
        {outbidSaleSelected && (
          <SaleMain
            outbidSale={outbidSaleSelected}
            setOutbidSaleSelected={setOutbidSaleSelected}
            outbidLocalStorageState={outbidLocalStorageState}
            setOutbidLocalStorageState={setOutbidLocalStorageState}
          />
        )}

        {/* {outbidSaleSelected && (
          <SaleDetails outbidSaleSelected={outbidSaleSelected} setOutbidSaleSelected={setOutbidSaleSelected} />
        )} */}
      </div>
    </MainLayout>
  );
}

/* ----- OUTBID SOLANA ----- */
function OutbidSolana() {
  const { i18 } = useLanguage();
  const [topFilter, setTopFilter] = useState<"all" | "upcoming" | "live" | "vesting" | "completed">("all");
  const routeParams = useParams();
  const [outbidSaleSelected, setOutbidSaleSelected] = useState<OutbidSale | null>(null);
  const [outbidSales, setOutbidSales] = useState<OutbidSale[]>([]);

  const topFilterOptions: FilterOption[] = [
    {
      label: i18("All", "pools.topFilter.all"),
      id: "all",
    },
    {
      label: i18("Upcoming", "pools.topFilter.upcoming"),
      id: "upcoming",
    },
    {
      label: i18("Live", "pools.topFilter.live"),
      id: "live",
    },
    {
      label: i18("Vesting", "pools.topFilter.vesting"),
      id: "vesting",
    },
    {
      label: i18("Completed", "pools.topFilter.completed"),
      id: "completed",
    },
  ];

  useEffect(() => {
    if (routeParams && routeParams.label) {
      const sale = outbidSales.find(sale => sale.name === routeParams.label);
      if (sale) {
        setOutbidSaleSelected(sale);
      }
    }
  }, [routeParams, outbidSales]);

  useEffect(() => {
    const fetchOutbidSales = async () => {
      const outbidSales = await useGetOutbidSales(topFilter);
      setOutbidSales(outbidSales);
    };
    fetchOutbidSales();
  }, [topFilter]);

  return (
    <MainLayout
      pageClass={clsx(styles.sales, "sales")}
      headTitle={i18("Outbid", "outbid.headTitle")}
      headDescription={i18("Outbid", "outbid.headDescription")}
    >
      <div
        style={{
          maxWidth: "64em",
          width: "100%",
          alignSelf: "center",
        }}
      >
        {!outbidSaleSelected && (
          <SolanaSalesSelectGrid
            topFilter={topFilter}
            setTopFilter={setTopFilter}
            topFilterOptions={topFilterOptions}
            outbidSales={outbidSales}
          />
        )}
        {!!outbidSaleSelected && (
          <SolanaSaleMain selectedOutbidSale={outbidSaleSelected} setSelectedOutbidSale={setOutbidSaleSelected} />
        )}
      </div>
    </MainLayout>
  );
}

export default Outbid;
